import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Save, XCircle } from "lucide-react";
import { LanguageField, BriefingField, AutonomyField, WorkflowField, OutlookField, GroupsField } from "./fields";

function Block({ title, children }) {
  return <section className="rounded-2xl border border-border bg-card p-5"><p className="mb-3 text-sm font-semibold text-foreground">{title}</p>{children}</section>;
}

export default function SettingsPanel({ status, onChange }) {
  const t = useT();
  const [settings, setSettings] = useState(null);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    api.get("/coach/assistant/settings").then(({ data }) => setSettings(data)).catch(() => setSettings({}));
    api.get("/coach/assistant/groups").then(({ data }) => setGroups(data.groups || [])).catch(() => {});
  }, []);
  if (!settings) return <Skeleton className="h-64 rounded-2xl" />;
  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/coach/assistant/settings", settings); toast.success(t("aipa.settings_saved")); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const cancelPkg = async () => {
    if (!window.confirm(t("aipa.cancel_confirm"))) return;
    setCanceling(true);
    try { await api.post("/coach/assistant/cancel"); toast.success(t("aipa.cancel_done")); onChange?.(); }
    catch (err) { toast.error(apiError(err)); } finally { setCanceling(false); }
  };

  return (
    <div className="space-y-4" data-testid="aipa-settings">
      <Block title={t("aipa.step_language")}><LanguageField value={settings.lang || "de"} onChange={(v) => set({ lang: v })} /></Block>
      <Block title={t("aipa.step_briefing")}><BriefingField settings={settings} set={set} /></Block>
      <Block title={t("aipa.step_autonomy")}><AutonomyField settings={settings} set={set} /></Block>
      <Block title={t("aipa.step_workflows")}><WorkflowField settings={settings} set={set} /></Block>
      <Block title={t("aipa.step_outlook")}><OutlookField settings={settings} set={set} /></Block>
      <Block title={t("aipa.step_groups")}><GroupsField settings={settings} set={set} groups={groups} /></Block>
      <div className="flex items-center justify-between">
        <Button className="gap-1.5 rounded-full" disabled={saving} onClick={save} data-testid="aipa-settings-save"><Save className="h-4 w-4" /> {saving ? t("aipa.saving") : t("aipa.save")}</Button>
        {status?.status !== "canceled" && (
          <Button variant="outline" className="gap-1.5 rounded-full text-rose-600" disabled={canceling} onClick={cancelPkg} data-testid="aipa-cancel-package"><XCircle className="h-4 w-4" /> {canceling ? t("aipa.canceling") : t("aipa.cancel_package")}</Button>
        )}
      </div>
    </div>
  );
}
